import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { MessageService, TreeNode } from 'primeng/api';

import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import { RequestButtonTypes } from '../../../../common/components/request-button/request-button.component';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import { createCsv } from 'src/app/common/components/reactive-form-builder/export/createCsv';
import { Filtering } from '../../../../service/filtering/filtering';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { StorageManager } from '../../../../common/storage-manager.class';
import { Utils } from '../../../../common/utils';

import { DashboardService } from '../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../service/api/fincas-api.service';
import { FincasService } from '../../../../service/fincas/fincas.service';

import { BaseView } from '../../../base-view';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import { HistoricoREAModel } from 'src/app/models/fincas/rea.model';

import { FincasEnvironmentDefinition } from './environment-variables';
import { FincasFormDefinition } from './finca-form/form-definition';

const DEFAULTS = {
	FIRST_CUADERNO_YEAR: Number('2016'),
	LECTURA_CONTADOR_ALERT_THRESHOLD: Number('6000'),
} as const;

@Component({
	selector: 'app-finca',
	standalone: false,
	templateUrl: './finca.component.html',
	styleUrls: ['./finca.component.scss'],
})
export class FincaComponent extends BaseView<FincasModel> implements OnInit {
	public form!: FincasFormDefinition;

	public readonly env = new FincasEnvironmentDefinition();

	public readonly deleteRequestButtonType = RequestButtonTypes.DELETE;

	public getRequest: RequestHandler<FincasModel[]>;

	public deleteRequest: RequestHandler<FincasModel>;

	public filter = new Filtering<FincasModel>();

	public historicoREA: BaseResponseModel<HistoricoREAModel> | null = null;

	public cols: ColInterface[] = [
		{
			field: 'id',
			header: 'Nº Finca',
			filter: '',
			visible: this.env.showNumeroFincas,
		},
		{
			field: 'cliente_nombre',
			header: this.env.clienteName,
			filter: '',
			visible: this.env.hasClients,
		},
		{
			field: 'nombre',
			header: 'Nombre ' + this.env.fincaName,
			filter: '',
		},
		{
			field: 'provincia',
			header: 'Provincia',
			filter: '',
		},
		{
			field: 'titular_nombre',
			header: 'Titular',
			filter: '',
			visible: !this.env.activateREA,
		},
		{
			field: 'representante_nombre',
			header: 'Representante',
			filter: '',
			visible: this.env.activateREA,
		},
		{
			field: 'telefono',
			header: 'Teléfono',
			filter: '',
		},
		{
			field: 'email',
			header: 'Email',
			filter: '',
		},
		{
			field: 'lectura_contador',
			header: 'Acumulado m3 ',
			visible: this.env.appName === 'abonosfolques',
		},
		{
			field: 'cooperativa',
			header: 'Cooperativa',
			filter: '',
			visible: this.env.showClientesCooperativa,
		},
	];

	private readonly menuItemId = 'finca';

	/**
	 * Creates an instance of the fincas component and configures the base request handlers.
	 *
	 * The constructor injects the required API, service, dashboard, message and routing
	 * dependencies, initializes the parent class with the default GET and DELETE requests,
	 * and assigns the local request references used by the component.
	 *
	 * @constructor
	 * @public
	 *
	 * @param fincasApi - API service used to execute finca-related HTTP requests.
	 * @param fincasSvc - Domain service used to manage finca-related business logic.
	 * @param dashboard - Shared dashboard service used by the parent class and this component.
	 * @param messageService - PrimeNG service used to display user-facing messages.
	 * @param router - Angular router used to navigate between application routes.
	 *
	 * @returns A new component instance configured with finca GET and DELETE requests.
	 *
	 * @remarks
	 * The constructor delegates common request handling to the parent class through `super`.
	 * After that, it also stores the same GET and DELETE request references locally in
	 * `getRequest` and `deleteRequest`.
	 *
	 * @example
	 * ```ts
	 * const component = new FincasComponent(
	 *   fincasApi,
	 *   fincasSvc,
	 *   dashboard,
	 *   messageService,
	 *   router
	 * );
	 * ```
	 *
	 * @see FincasApiService
	 * @see FincasService
	 * @see DashboardService
	 * @see MessageService
	 * @see Router
	 */
	public constructor(
		private readonly fincasApi: FincasApiService,
		private readonly fincasSvc: FincasService,
		public dashboard: DashboardService,
		public messageService: MessageService,
		private readonly router: Router,
	) {
		super(dashboard, fincasApi.fincasGET, fincasApi.fincasDELETE);

		this.getRequest = this.fincasApi.fincasGET;
		this.deleteRequest = this.fincasApi.fincasDELETE;
	} // ();

	/**
	 * Initializes the component state after Angular has created the instance.
	 *
	 * This lifecycle hook selects the active dashboard submenu item for the fincas
	 * section and registers a basic filter to remove empty tree nodes from the
	 * loaded finca data.
	 *
	 * @public
	 * @override
	 * @returns Nothing.
	 *
	 * @remarks
	 * The filter safely handles `void` or undefined-like input by falling back to an
	 * empty array before removing falsy entries.
	 *
	 * @example
	 * ```ts
	 * component.ngOnInit();
	 * ```
	 *
	 * @see OnInit
	 * @see DashboardService.setSubmenuItem
	 * @see TreeNode
	 * @see FincasModel
	 */
	public ngOnInit(): void {
		this.dashboard.setSubmenuItem('fincas', this.menuItemId);
		this.filter.addFilter((data: TreeNode<FincasModel>[] | void) => (data || []).filter(Boolean));
	} // ();

	/**
	 * Displays the requested modal with the selected finca data.
	 *
	 * This method prepares the form definition according to the requested modal type,
	 * stores the selected finca model and ensures that only the target modal remains
	 * visible.
	 *
	 * @public
	 * @override
	 *
	 * @param modal - Modal identifier to display. When its value is `'delete'`, the
	 * form is initialized as a deletion form; otherwise, it is initialized as an
	 * informative form.
	 * @param data - Finca data model associated with the selected modal action.
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The method also closes any other currently visible modal by iterating through
	 * the `canShow` visibility map.
	 *
	 * @example
	 * ```ts
	 * this.show('delete', finca);
	 * this.show('info', finca);
	 * ```
	 *
	 * @see FincasModel
	 * @see FincasFormDefinition
	 * @see FormType
	 */
	public override show(modal: string, data: FincasModel): void {
		const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;

		this.form = new FincasFormDefinition(formType, null);
		this.model = data;
		this.canShow[modal] = true;

		for (const key in this.canShow) {
			if (key !== modal && (this.canShow[key] ?? false)) {
				this.canShow[key] = false;
			} // if();
		} // for();
	} // ();

	/**
	 * Starts the finca creation flow.
	 *
	 * This method checks whether a client is currently selected before navigating to
	 * the finca creation page. When no client is selected and the current user can
	 * access clients, it opens the client selection dialog instead of navigating.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The navigation promise is explicitly ignored with the `void` operator because
	 * no post-navigation handling is required in this flow.
	 *
	 * @example
	 * ```ts
	 * this.add();
	 * ```
	 *
	 * @see StorageManager.getClient
	 * @see DashboardService.openClientDialog
	 * @see Router.navigate
	 */
	public add(): void {
		if (!StorageManager.getClient() && this.env.userCanSeeClientes) {
			this.dashboard.openClientDialog();
			return;
		} // if();

		void this.router.navigate(['dashboard', 'crear-finca']);
	} // ();

	/**
	 * Navigates to the finca edition page for the selected finca.
	 *
	 * This method uses the selected finca identifier to build the edition route.
	 *
	 * @public
	 *
	 * @param data - Finca model containing the identifier of the finca to edit.
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The navigation promise is explicitly ignored with the `void` operator because
	 * the component does not need to handle the navigation result.
	 *
	 * @example
	 * ```ts
	 * this.edit(finca);
	 * ```
	 *
	 * @see FincasModel
	 * @see Router.navigate
	 */
	public edit(data: FincasModel): void {
		void this.router.navigate(['dashboard', 'editar-finca', data.id]);
	} // ();

	/**
	 * Navigates to the finca duplication page for the selected finca.
	 *
	 * This method uses the selected finca identifier to build the duplication route.
	 *
	 * @public
	 *
	 * @param data - Finca model containing the identifier of the finca to duplicate.
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The navigation promise is explicitly ignored with the `void` operator because
	 * the component does not need to handle the navigation result.
	 *
	 * @example
	 * ```ts
	 * this.duplicate(finca);
	 * ```
	 *
	 * @see FincasModel
	 * @see Router.navigate
	 */
	public duplicate(data: FincasModel): void {
		void this.router.navigate(['dashboard', 'duplicar-finca', data.id]);
	} // ();

	/**
	 * Refreshes the finca list data.
	 *
	 * This method executes the configured GET request handler to reload the current
	 * finca list safely.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The request is executed through `safePerform` so request handling remains
	 * centralized and protected against expected request errors.
	 *
	 * @example
	 * ```ts
	 * this.refreshList();
	 * ```
	 *
	 * @see RequestHandler.safePerform
	 */
	public refreshList(): void {
		this.getRequest.safePerform();
	} // ();

	/**
	 * Clears the dependent sector-related form selections.
	 *
	 * This method resets the selected sector, parcel and variety values so the form
	 * does not keep stale dependent data after a filtering change.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * This method only clears the current selections. It does not remove the
	 * available list options from the form definition.
	 *
	 * @example
	 * ```ts
	 * this.filterSectores();
	 * ```
	 *
	 * @see FincasFormDefinition
	 */
	public filterSectores(): void {
		this.form.lists.sectores.selected = null;
		this.form.lists.parcelas.selected = null;
		this.form.lists.variedades.selected = null;
	} // ();

	/**
	 * Applies the global visual transformation to a displayed value.
	 *
	 * This method currently returns the received value without applying any
	 * transformation, acting as a passthrough formatter for global visual rendering.
	 *
	 * @public
	 *
	 * @param value - Text value to transform before displaying it.
	 *
	 * @returns The same text value received as input.
	 *
	 * @remarks
	 * This method can be extended later to apply shared formatting rules without
	 * changing the components that already consume it.
	 *
	 * @example
	 * ```ts
	 * const visibleValue = this.globalVisualTransform('Finca principal');
	 * ```
	 */
	public globalVisualTransform(value: string): string {
		return value;
	} // ();

	/**
	 * Resolves the visual styles applied to a table cell according to its value and column.
	 *
	 * This method centralizes conditional styling for finca list columns. It highlights
	 * meter readings that reach the configured alert threshold, aligns numeric-like
	 * values and phone numbers with additional right margin, and applies the default
	 * centered alignment for the rest of the values.
	 *
	 * @public
	 *
	 * @param value - Cell value used to determine whether numeric or alert styles apply.
	 * @param col - Column definition that identifies the field being rendered.
	 *
	 * @returns The style configuration that must be applied to the rendered cell.
	 *
	 * @remarks
	 * The `lectura_contador` field is highlighted in red when its numeric value is
	 * greater than or equal to `DEFAULTS.LECTURA_CONTADOR_ALERT_THRESHOLD`.
	 *
	 * Phone values are treated like numeric values for visual alignment purposes,
	 * even if their format is not strictly numeric.
	 *
	 * @example
	 * ```ts
	 * const styles = this.globalConditionalStyle(1500, column);
	 * ```
	 *
	 * @see ColInterface
	 * @see ColStylesInterface
	 * @see DEFAULTS.LECTURA_CONTADOR_ALERT_THRESHOLD
	 * @see Utils.isNumber
	 */
	public globalConditionalStyle(value: string | number, col: ColInterface): ColStylesInterface {
		if (col.field === 'lectura_contador' && +value >= DEFAULTS.LECTURA_CONTADOR_ALERT_THRESHOLD) {
			return { textAlign: 'center', marginRight: '60px', color: 'red' };
		} // if();

		if (Utils.isNumber(value?.toString() ?? '') || col.field === 'telefono') {
			return { textAlign: 'center', marginRight: '60px' };
		} // if();

		return { textAlign: 'center' };
	} // ();

	/**
	 * Generates the GML file for the selected finca after validating its availability.
	 *
	 * This method first verifies whether the GML generation can be performed for the
	 * provided finca identifier. If the validation response is successful, the GML
	 * generation process is started. Otherwise, the user is notified with the
	 * validation message returned by the service.
	 *
	 * @public
	 *
	 * @param data - Object containing the identifier of the finca used to verify and
	 * generate the GML file.
	 * @param data.id - Finca identifier.
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The verification observable is converted into a promise with `firstValueFrom`
	 * because the method only needs the first emitted response.
	 *
	 * Errors produced during the verification request are logged to the console, while
	 * validation errors returned by the backend are shown to the user through `alert`.
	 *
	 * @example
	 * ```ts
	 * this.generaGML({ id: finca.id });
	 * ```
	 *
	 * @throws This method does not intentionally throw errors. Request errors are
	 * handled internally in the promise `catch` block.
	 *
	 * @see firstValueFrom
	 * @see FincasService.verificaGML
	 * @see FincasService.generaGML
	 */
	public generaGML(data: { id: string }): void {
		firstValueFrom(this.fincasSvc.verificaGML(data.id))
			.then((response) => {
				if (response.success) {
					this.fincasSvc.generaGML(data.id);
					return;
				} // if();

				alert(response.mensaje);
			})
			.catch((error: unknown) => {
				console.error('Error generating GML', error);
			});
	} // ();

	/**
	 * Downloads the currently filtered finca list as a CSV file.
	 *
	 * This method creates an export form definition to obtain the CSV field names,
	 * labels and filename. It then generates the CSV content from the current
	 * filtered finca data, assigns it to the configured download link and triggers
	 * the browser download.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The method expects an anchor element with the `csvLink` identifier to exist in
	 * the DOM. If the element is not found, the download is cancelled and an error is
	 * logged.
	 *
	 * The filtered value is cast to `FincasModel[]` because the CSV generator expects
	 * finca rows as its data source.
	 *
	 * @example
	 * ```ts
	 * this.downloadCsv();
	 * ```
	 *
	 * @throws This method does not intentionally throw errors. Missing DOM elements
	 * are handled internally by logging an error and returning early.
	 *
	 * @see FincasFormDefinition
	 * @see FormType.EXPORT
	 * @see createCsv
	 * @see FincasModel
	 * @see HTMLAnchorElement
	 */
	public downloadCsv(): void {
		this.form = new FincasFormDefinition(+FormType.EXPORT, null);
		const csvContent = createCsv(this.form.fieldNames, this.form.fieldLabels, this.filtering.value as FincasModel[]);
		const csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

		if (!csvLink) {
			console.error('CSV download link was not found.');
			return;
		}// if();

		csvLink.download = `${this.form.formFields.name}.csv`;
		csvLink.href = encodeURI(csvContent);
		csvLink.click();
	}// ();
}// class;
